/**
 * Pairing a line typed onto a material order with the quoted part it means.
 *
 * `compareOrderToQuote` pairs by product, then by exact folded name. A line
 * somebody typed — "galv screws 1.5in" — carries no product and never folds to
 * "1 1/2" Metal to Wood Screws Galvanized", so the check reports it twice: the
 * quoted screw not ordered, the typed line not quoted. Both are false alarms,
 * and a banner full of false alarms is a banner nobody reads.
 *
 * Jev (TypeSafe) reads the two wordings and says whether they are the same part.
 * It only proposes a pairing. Which lines are asked about, which quoted parts
 * are offered, the threshold a pairing must clear, and every number compared
 * afterwards stay here, in code (ADR 0153).
 *
 * The call itself is made by the `quote-match` Edge Function, which holds the
 * key. It cannot import this workspace, so `supabase/functions/quote-match/
 * request.ts` transcribes the two functions that build the request and read the
 * answers, and `supabase/test/quote-match-request.test.ts` binds them together.
 */

import type { QuoteCheckResult } from "./quoteCheck.js";

/** The model, as TypeSafe names it. */
export const QUOTE_MATCH_MODEL = "jev-latest";

/**
 * How sure Jev must be before its pairing is used.
 *
 * A wrong pairing hides a real difference, which is worse than a false alarm,
 * so this sits at the top of TypeSafe's "proceed with care" band rather than in
 * it. Every suggestion is kept whatever its probability, so this can be tuned
 * on real orders without asking again.
 */
export const QUOTE_MATCH_THRESHOLD = 0.8;

/** The option meaning "none of these is the same part". */
export const QUOTE_MATCH_NONE = "none";

/**
 * The most typed lines, and quoted parts, one request may ask about. The
 * `quote-match` function refuses more; the screen sends lines in batches of
 * this size rather than have a large order never matched at all.
 */
export const QUOTE_MATCH_MAX_LINES = 60;
export const QUOTE_MATCH_MAX_PARTS = 254;

/** An order line as the question sees it. */
export interface MatchOrderLine {
  id: string;
  name: string | null;
  qty: unknown;
  unit: string | null;
}

/** A quoted part nothing on the order has claimed. */
export interface MatchQuotedPart {
  id: string;
  name: string | null;
  category: string | null;
  unit: string | null;
}

/** One proposed pairing, as it is stored in `quote_match_suggestions`. */
export interface QuoteMatchSuggestion {
  order_item_id: string;
  /** The order line's name when it was asked about. A rename asks again. */
  order_item_name: string | null;
  /** The quoted part Jev chose, or null for none of them. */
  product_id: string | null;
  /** The probability of that choice, 0 to 1. */
  probability: number;
  /** False once a person has said it is not the same part. */
  accepted?: boolean | null;
}

/** A pairing the check should use. */
export interface QuotePairing {
  orderItemId: string;
  productId: string;
  probability: number;
}

/**
 * What to ask about: typed lines the check could not place, and the quoted
 * parts nothing claimed.
 *
 * Only lines with no product. A line that names a product and still did not
 * pair names a *different* product — a painted panel on a galvalume quote —
 * and that is a real difference, not a wording problem for a model to smooth
 * over.
 */
export function quoteMatchCandidates(result: QuoteCheckResult): {
  itemIds: string[];
  productIds: string[];
} {
  if (result.status !== "compared") return { itemIds: [], productIds: [] };
  const itemIds = result.discrepancies
    .filter((d) => d.kind === "not_quoted" && d.productId === null && d.itemId)
    .map((d) => d.itemId!);
  const productIds = result.discrepancies
    .filter((d) => d.kind === "not_ordered" && d.productId)
    .map((d) => d.productId!);
  if (itemIds.length === 0 || productIds.length === 0) {
    return { itemIds: [], productIds: [] };
  }
  return { itemIds, productIds };
}

/** The option key for the nth quoted part. Keys go to code, not the model. */
const optionKey = (index: number): string => `part_${index + 1}`;

const partLabel = (part: MatchQuotedPart): string =>
  [part.name, part.unit ? `(sold by ${part.unit})` : null]
    .filter(Boolean)
    .join(" ");

/**
 * The TypeSafe request: one Choice per order line, all over the same state, so
 * they run together.
 *
 * Wording only. Quantities are left out on purpose — ten bundles and twelve are
 * the same part, and a model that saw the numbers might pair on them.
 */
export function quoteMatchRequest(
  lines: readonly MatchOrderLine[],
  parts: readonly MatchQuotedPart[],
): {
  model: string;
  state: unknown;
  questions: Record<string, unknown>;
} {
  const state = {
    context:
      "A roofing company's material order, checked against the parts on the customer's quote. " +
      "Somebody typed the order lines by hand; the quoted parts come from the company's catalogue.",
    order_lines: lines.map((line) => ({
      name: line.name ?? "",
      unit: line.unit ?? "",
    })),
    quoted_parts: parts.map((part) => ({
      name: part.name ?? "",
      category: part.category ?? "",
      unit: part.unit ?? "",
    })),
  };

  const criteria: Record<string, string> = {};
  parts.forEach((part, index) => {
    criteria[optionKey(index)] =
      `The same part as \`quoted_parts[${index}]\`: ${partLabel(part)}`;
  });
  criteria[QUOTE_MATCH_NONE] =
    "None of the quoted parts. The line is a different product, or a different size, gauge, colour or finish of one.";

  const questions: Record<string, unknown> = {};
  lines.forEach((line, index) => {
    questions[line.id] = {
      type: "choice",
      instructions:
        `Which quoted part is order line \`order_lines[${index}]\` the same part as? ` +
        'Abbreviations, word order, supplier shorthand and inches written as 1.5in or 1 1/2" can all name the same part. ' +
        "A different size, gauge, length, colour, finish or product is not the same part.",
      criteria,
    };
  });

  return { model: QUOTE_MATCH_MODEL, state, questions };
}

/**
 * Read TypeSafe's answers back into suggestions, one per order line asked.
 *
 * A line with no usable answer — missing, malformed, an option that does not
 * exist — becomes a suggestion of none at probability 0, so it is stored as
 * asked and not asked again on every open. A real "none" keeps the
 * probability Jev gave it.
 */
export function readQuoteMatch(
  answers: unknown,
  lines: readonly MatchOrderLine[],
  parts: readonly MatchQuotedPart[],
): QuoteMatchSuggestion[] {
  const byKey = new Map(parts.map((part, index) => [optionKey(index), part]));
  const all = (answers ?? {}) as Record<string, unknown>;

  return lines.map((line) => {
    const answer = all[line.id] as
      { type?: unknown; choice?: unknown; probabilities?: unknown } | undefined;
    const choice = typeof answer?.choice === "string" ? answer.choice : null;
    const probabilities = (answer?.probabilities ?? {}) as Record<
      string,
      unknown
    >;
    const probability = Number(choice ? probabilities[choice] : 0);
    const part = choice ? byKey.get(choice) : undefined;
    // "none" keeps its probability too: how sure Jev was that nothing fits is
    // worth reading later. An option that does not exist keeps nothing.
    const known = part !== undefined || choice === QUOTE_MATCH_NONE;

    return {
      order_item_id: line.id,
      order_item_name: line.name,
      product_id: part ? part.id : null,
      probability:
        known && Number.isFinite(probability)
          ? Math.min(1, Math.max(0, probability))
          : 0,
    };
  });
}

/**
 * The stored suggestions the check should use right now.
 *
 * A suggestion counts only while it still describes the order: the line is
 * unchanged since it was asked, it chose a part, it clears the threshold, and
 * nobody has overruled it. Where two lines chose the same part the likelier
 * one keeps it — one quoted part cannot be ordered twice by pairing.
 */
export function usablePairings(
  suggestions: readonly QuoteMatchSuggestion[],
  items: readonly { id: string; name?: string | null }[],
  threshold: number = QUOTE_MATCH_THRESHOLD,
): QuotePairing[] {
  const nameOf = new Map(items.map((item) => [item.id, item.name ?? null]));
  const eligible = suggestions
    .filter(
      (s) =>
        s.product_id !== null &&
        s.accepted !== false &&
        s.probability >= threshold &&
        nameOf.has(s.order_item_id) &&
        nameOf.get(s.order_item_id) === s.order_item_name,
    )
    .sort((a, b) => b.probability - a.probability);

  const taken = new Set<string>();
  const pairings: QuotePairing[] = [];
  for (const s of eligible) {
    if (taken.has(s.product_id!)) continue;
    taken.add(s.product_id!);
    pairings.push({
      orderItemId: s.order_item_id,
      productId: s.product_id!,
      probability: s.probability,
    });
  }
  return pairings;
}

/**
 * Which of the candidate lines still need asking: never asked, or renamed
 * since they were.
 */
export function linesToAsk(
  itemIds: readonly string[],
  items: readonly { id: string; name?: string | null }[],
  suggestions: readonly QuoteMatchSuggestion[],
): string[] {
  const asked = new Map(
    suggestions.map((s) => [s.order_item_id, s.order_item_name]),
  );
  const nameOf = new Map(items.map((item) => [item.id, item.name ?? null]));
  return itemIds.filter(
    (id) => !asked.has(id) || asked.get(id) !== nameOf.get(id),
  );
}
